/* eslint-disable react/prop-types */
import { useState, useMemo } from "react";
import { Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import testIds from "constants/testIds";
import useTableRequest from "hooks/useTableRequest";

// import component
import { getUserRoles } from "api";
import Button from "components/atoms/Button/Button";
import usePermissions from "hooks/usePermissions";
import EditUserRole from "./EditUserRole";
import "./userRole.scss";

const UserRole = function () {
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  const [userId, setUserId] = useState(null);
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_user_roles);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="mb-0 mr-15 pl-2 font-weight-bold">
            <FormattedMessage id="settings.user-role.roleNameHeader" defaultMessage="Role" />
          </span>
        ),
        accessor: "name", // accessor is the "key" in the data
        sortable: true,
        Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0 mr-1">{value}</p>,
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="settings.user-role.userWithThisRoleHeader" defaultMessage="Users" />
          </span>
        ),
        sortable: true,
        accessor: "no_of_users",
        Cell: ({ value }) => (
          <p className="pt-8 pb-8 mb-0" style={{ marginRight: "150px" }}>
            {value}
          </p>
        ),
      },
      {
        Header: (
          <span className="mb-0 mr-15">
            <FormattedMessage id="settings.user-role.permissions" defaultMessage="Permissions" />
          </span>
        ),
        id: "permission_labels",
        Cell: ({ value }) => <span className="pt-8 pb-8 mb-0">{value}</span>,
        accessor: (data) => (
          <Row>
            {data.permissions.map((item) => (
              <div key={item.name}>
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip id="button-tooltip">{item.description}</Tooltip>}
                >
                  {item.is_active ? (
                    <Button
                      style={{ height: "24px", backgroundColor: "black", border: "none" }}
                      className="rounded-pill mt-1 mb-1 mr-2"
                    >
                      {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                    </Button>
                  ) : (
                    <Button
                      style={{ height: "24px", backgroundColor: "white", border: "1px solid black", color: "black" }}
                      className="rounded-pill mt-1 mb-1 mr-2"
                    >
                      {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                    </Button>
                  )}
                </OverlayTrigger>
              </div>
            ))}
          </Row>
        ),
      },
      {
        Header: (
          <span className="mb-0 mr-0">
            <FormattedMessage id="settings.user-role.description" defaultMessage="Description" />
          </span>
        ),
        accessor: "description",
        sortable: true,
        Cell: ({ value }) => <div className="pt-8 pb-8 ">{value}</div>,
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const closeEditRoleDialog = () => {
    setShowEditRoleDialog(false);
  };

  const { instance, fetching, totalData, refetch } = useTableRequest(getUserRoles, columns, {
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
    }),
  });

  return (
    <>
      {showEditRoleDialog && (
        <EditUserRole
          viewOnly={viewOnly}
          count={totalData}
          showEditRoleDialog={showEditRoleDialog}
          closeEditRoleDialog={closeEditRoleDialog}
          id={userId}
          refetch={refetch}
        />
      )}
      <div className="user-role-container">
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          onRowClick={(row) => {
            setUserId(row.original.id);
            setShowEditRoleDialog(true);
          }}
          disablePagination
          headerRight={
            <div className="d-flex justify-content-between mt-3 w-100">
              <div>
                <GlobalTableSearch
                  currentFilters={instance.state.globalFilter}
                  setFilters={instance.setGlobalFilter}
                  filterKey="query"
                  eventContext="User roles"
                />
              </div>
              <div className="d-flex align-items-center">
                {!viewOnly && (
                  <Button
                    onClick={() => {
                      setUserId(null);
                      setShowEditRoleDialog(true);
                    }}
                    data-testid={testIds.ADDRESS_BOOK_ADD_BUTTON}
                  >
                    <FormattedMessage id="settings.user-role.addRole" defaultMessage="Add role" />
                  </Button>
                )}
              </div>
            </div>
          }
        />
      </div>
    </>
  );
};

export default UserRole;
